import React, { useContext, useEffect } from 'react'

import { Account } from '@driftt/api-types'
import { Accordion, Flex, SimpleGrid } from '@driftt/dds'

import { driftLoadingStateBuilder } from 'components/States'
import { CurrentUserContext } from 'context/currentUserContext'
import { AccountSiteVisitInfo } from 'hooks/useAccountDetails'
import { AccountActivity, CompanyData, ScoreBreakdown } from 'modules/accountActivity/domain'
import AccountInfoRow from 'pages/CCRMAccountDetailsPage/AccountDetailsHeader/AccountInfoRow'
import { bucketFitScore, bucketRecommendation } from 'utils/bucketingUtil'
import { segmentTrack,TrackType } from 'utils/tracking'

import AIEngagementSummary from './AIEngagementSummary'
import RecommendedContacts from './RecommendedContacts'
import { ResearchTopics } from './ResearchTopics'
import { TopPages } from './TopPages'

interface OverviewTabProps {
  accountId: string
  accountData: Account | undefined
  companyData: CompanyData | undefined
  explainData: AccountActivity | undefined
  accountFitScore: number | undefined
  scoreBreakdown: ScoreBreakdown | undefined
  topPagesAndTopics: { topPages: AccountSiteVisitInfo[], topics: string[]} | undefined
  numOfOpportunities: React.ReactNode
  isLoading: boolean
  trackAction: (action: string) => void
}

const OverviewTab: React.FC<OverviewTabProps> = ({
  accountId,
  accountData,
  companyData,
  explainData,
  accountFitScore,
  scoreBreakdown,
  topPagesAndTopics,
  numOfOpportunities,
  isLoading,
  trackAction
}) => {
  const currentUser = useContext(CurrentUserContext)

  useEffect(() => {
    segmentTrack(TrackType.ACCOUNT_FIT_DRAWER_VIEWED, {
      accountId,
      accountFitBucketedScore: bucketFitScore(accountFitScore),
      accountFitScore: accountFitScore,
      userId: currentUser?.id,
      orgId: currentUser?.orgId,
    })
  }, [accountFitScore, accountId, currentUser?.id, currentUser?.orgId])

  const LoadingComponent = driftLoadingStateBuilder('Loading account details...')

  if (isLoading) {
    return <LoadingComponent/>
  }

  return (
    <Flex direction='column' fullHeight fullWidth css={{ 
      backgroundColor: '$gray2', 
      padding: '16px',
      height: 'calc(100% - 31px)',
      overflow: 'scroll',
    }}>
      <Accordion.Group 
        canOpenMultiple 
        variant='card'
        headerVariant='h3'
        defaultValue={['details', 'engagement-summary', 'recommended-contacts']}>
        <Accordion.Item
          id="details"
          header='Details'
        >
          <SimpleGrid columns={4} gap={1}>
            <AccountInfoRow 
              account={accountData}
              company={companyData}
              accountFitScore={accountFitScore}
              numOfOpportunities={numOfOpportunities}
              accountEngagement={explainData?.normalizedEngagementScore}
              showEngagementScore={true}
              maxItemWidth={150} 
              scoreBreakdown={scoreBreakdown}/>
          </SimpleGrid>
        </Accordion.Item>
        <AIEngagementSummary 
          accountId={accountId} 
          recommendation={bucketRecommendation(explainData?.accountScoredActivity?.recommendation)}/>
        <Accordion.Item
          id="recommended-contacts"
          header="Recommended contacts"
          subheader="Known contacts that are the most active on your site"
        >
          <RecommendedContacts accountId={accountId} trackAction={trackAction}/>
        </Accordion.Item>
        <SimpleGrid columns={2} gap={2}>
          <TopPages topPages={topPagesAndTopics?.topPages} trackAction={trackAction}/>
          <ResearchTopics topics={topPagesAndTopics?.topics}/>
        </SimpleGrid>
      </Accordion.Group>
    </Flex>
  )
}

export default OverviewTab
